import {
  abandonGuestPayment,
  confirmGuestPayment,
  createGuestPayment,
  getOrderPayment,
  isOpenPaymentStatus,
  PaymentView,
  refreshGuestPayment,
} from '../services/paymentService';
import restaurantService from '../services/restaurantService';
import { isEmbeddedKioskWebView } from '../components/Kiosk/kioskPaths';

function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) {
      resolve();
      return;
    }
    const el = document.createElement('script');
    el.src = src;
    el.async = true;
    el.onload = () => resolve();
    el.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.head.appendChild(el);
  });
}

function httpUrl(value?: string | null): string | null {
  if (!value) return null;
  const url = String(value).trim();
  if (url.startsWith('https://') || url.startsWith('http://')) return url;
  return null;
}

function submitHostedForm(data: Record<string, string>) {
  const action = httpUrl(data.action);
  if (!action) {
    throw new Error('Checkout form is missing');
  }
  const form = document.createElement('form');
  form.method = 'POST';
  form.action = action;
  form.target = '_self';
  Object.keys(data).forEach((key) => {
    if (key === 'action') return;
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = key;
    input.value = data[key] ?? '';
    form.appendChild(input);
  });
  document.body.appendChild(form);
  form.submit();
}

async function bestEffortAbandon(paymentId: string, reason: 'FAILED' | 'CANCELLED') {
  try {
    await abandonGuestPayment(paymentId, reason);
  } catch {
    // Webhook/refresh remains source of truth if abandon fails.
  }
}

declare global {
  interface Window {
    Razorpay?: new (options: Record<string, unknown>) => {
      open: () => void;
      on: (event: string, handler: () => void) => void;
    };
    Cashfree?: (opts: { mode: string }) => {
      checkout: (opts: { paymentSessionId: string; redirectTarget: string }) => Promise<{
        error?: { message?: string } | string;
        redirect?: boolean;
        paymentDetails?: { paymentMessage?: string };
      }>;
    };
  }
}

export async function launchGuestCheckout(
  view: PaymentView,
  opts?: { name?: string; description?: string }
): Promise<PaymentView | { redirected: true }> {
  const type = view.checkout?.type || '';
  const data = view.checkout?.data || {};
  if (type === 'PAYMENT_LINK' || type === 'PHONEPE_REDIRECT') {
    const url = httpUrl(view.shareUrl) || httpUrl(data.url) || httpUrl(data.action);
    if (!url) {
      throw new Error('Payment link is missing');
    }
    window.location.assign(url);
    return { redirected: true };
  }
  if (type === 'PAYU_HOSTED') {
    submitHostedForm(data);
    return { redirected: true };
  }
  if (type === 'CASHFREE_CHECKOUT') {
    await loadScript('https://sdk.cashfree.com/js/v3/cashfree.js');
    if (!window.Cashfree) {
      throw new Error('Cashfree checkout could not be loaded');
    }
    // The backend decides the environment (sandbox vs production) from the merchant's API base URL;
    // a sandbox session id opened in production mode (or vice versa) fails at Cashfree.
    const mode = data.mode === 'sandbox' || data.mode === 'production' ? data.mode : null;
    if (!mode) {
      throw new Error('Cashfree checkout mode is missing');
    }
    if (!data.paymentSessionId) {
      throw new Error('Cashfree payment session is missing');
    }
    const cashfree = window.Cashfree({ mode });
    // Delivery checkout awaits place-order plus the gateway, which is often past the browser's
    // user-gesture window. Cashfree then resolves instead of navigating. A resolved call means this
    // page is still here, so open the payment modal on the page.
    const inline = await cashfree.checkout({
      paymentSessionId: data.paymentSessionId,
      redirectTarget: '_self',
    });
    if (inline?.paymentDetails) {
      return refreshGuestPayment(view.paymentId);
    }
    const modal = await cashfree.checkout({
      paymentSessionId: data.paymentSessionId,
      redirectTarget: '_modal',
    });
    if (modal?.error) {
      const message = typeof modal.error === 'string' ? modal.error : modal.error.message;
      throw new Error(message || 'Payment page could not be opened');
    }
    if (modal?.paymentDetails) {
      return refreshGuestPayment(view.paymentId);
    }
    if (modal?.redirect) {
      throw new Error('Payment page could not be opened');
    }
    return { redirected: true };
  }
  if (type === 'RAZORPAY_CHECKOUT_JS' || type === 'RAZORPAY_CHECKOUT') {
    const orderId = data.order_id || view.providerOrderId;
    const amountPaise = Number(data.amount || view.amountPaise);
    if (!data.key || !orderId || !Number.isFinite(amountPaise) || amountPaise <= 0) {
      throw new Error('Razorpay checkout could not be opened');
    }
    await loadScript('https://checkout.razorpay.com/v1/checkout.js');
    const rzpResponse = await new Promise<{
      razorpay_order_id: string;
      razorpay_payment_id: string;
      razorpay_signature: string;
    }>((resolve, reject) => {
      if (!window.Razorpay) {
        reject(new Error('Razorpay checkout could not be loaded'));
        return;
      }
      // payment.failed fires for each declined attempt while the modal stays open for a retry, so it
      // must not abandon the payment. Only the modal closing ends the checkout.
      let attemptFailed = false;
      const embeddedKiosk = isEmbeddedKioskWebView();
      const rzp = new window.Razorpay({
        key: data.key,
        amount: amountPaise,
        currency: data.currency || view.currency || 'INR',
        name: opts?.name || 'Peppar',
        description: opts?.description || 'Order payment',
        order_id: orderId,
        redirect: embeddedKiosk ? false : undefined,
        handler: (response: {
          razorpay_order_id: string;
          razorpay_payment_id: string;
          razorpay_signature: string;
        }) => resolve(response),
        modal: {
          ondismiss: () => {
            const reason = attemptFailed ? 'FAILED' : 'CANCELLED';
            void bestEffortAbandon(view.paymentId, reason).finally(() => {
              reject(new Error(attemptFailed ? 'Payment failed' : 'Payment cancelled'));
            });
          },
        },
        theme: { color: '#F5C21A' },
      });
      rzp.on('payment.failed', () => {
        attemptFailed = true;
      });
      rzp.open();
    });
    return confirmGuestPayment(view.paymentId, {
      orderId: rzpResponse.razorpay_order_id,
      paymentId: rzpResponse.razorpay_payment_id,
      signature: rzpResponse.razorpay_signature,
    });
  }
  if (data.action) {
    submitHostedForm(data);
    return { redirected: true };
  }
  throw new Error(`Unsupported checkout type: ${type || 'unknown'}`);
}

/** Guest left a hosted checkout open. Cancel it so the order can take more items or another payment. */
export async function promptCancelOpenPayment(appName: string): Promise<boolean> {
  const ongoing = await restaurantService.getOngoingOrder(appName);
  const order = ongoing?.order;
  const restroId = order?.userOrder?.restroId;
  const orderId = order?.orderId || order?.userOrder?.id;
  if (!restroId || !orderId) {
    return false;
  }
  const payment = await getOrderPayment(orderId, restroId);
  if (!payment || !isOpenPaymentStatus(payment.status)) {
    return false;
  }
  const cancel = window.confirm(
    'A payment is still open for this order. Cancel it so you can add items or pay another way?'
  );
  if (!cancel) {
    return false;
  }
  await abandonGuestPayment(payment.paymentId, 'CANCELLED');
  return true;
}

function paymentErrorMessage(err: unknown): string {
  const data = (err as { response?: { data?: { message?: string; code?: string } } })?.response?.data;
  return data?.message || (err as { message?: string })?.message || 'Payment failed';
}

function paymentErrorCode(err: unknown): string {
  return (err as { response?: { data?: { code?: string } } })?.response?.data?.code || '';
}

async function resumeWhenCheckoutReady(opts: {
  restroId: number;
  orderId: number;
  restaurantName?: string;
  orderCode?: string | number;
  returnUrl?: string;
}): Promise<PaymentView | { redirected: true } | null> {
  for (let attempt = 0; attempt < 4; attempt += 1) {
    if (attempt > 0) {
      await new Promise((resolve) => setTimeout(resolve, 700));
    }
    const resumed = await resumeGuestOrderPayment(opts);
    if (resumed) return resumed;
  }
  return null;
}

/**
 * Opens gateway checkout, and if a previous attempt is still open (delivery double-tap, or the
 * gateway call was still saving the payment page), reopens that checkout instead of stopping.
 */
export async function startOrResumeGuestOrderPayment(opts: {
  restroId: number;
  orderId: number;
  restaurantName?: string;
  orderCode?: string | number;
  returnUrl?: string;
}): Promise<PaymentView | { redirected: true }> {
  try {
    return await startGuestOrderPayment(opts);
  } catch (err) {
    const message = paymentErrorMessage(err);
    if (message === 'Payment cancelled' || message === 'Payment failed') {
      throw err;
    }
    const inFlight = paymentErrorCode(err) === 'PAYMENT_IN_FLIGHT';
    const canReopen =
      inFlight ||
      /could not be opened|missing|unsupported checkout|checkout form/i.test(message);
    if (!canReopen) {
      throw err;
    }
    const resumed = await resumeWhenCheckoutReady(opts);
    if (resumed) return resumed;
    throw err;
  }
}

export async function startGuestOrderPayment(opts: {
  restroId: number;
  orderId: number;
  restaurantName?: string;
  orderCode?: string | number;
  returnUrl?: string;
}): Promise<PaymentView | { redirected: true }> {
  const embeddedKiosk = isEmbeddedKioskWebView();
  const view = await createGuestPayment({
    restroId: opts.restroId,
    orderId: opts.orderId,
    // HOSTED breaks Razorpay modal checkout in the kiosk WebView; use JS checkout like the default guest flow.
    checkoutMode: embeddedKiosk ? 'CHECKOUT_JS' : 'HOSTED',
    returnUrl: opts.returnUrl,
  });
  return launchGuestCheckout(view, {
    name: opts.restaurantName,
    description: `Order ${opts.orderCode || opts.orderId}`,
  });
}

/** Re-open gateway checkout when createGuestPayment conflicts with an existing open attempt. */
export async function resumeGuestOrderPayment(opts: {
  restroId: number;
  orderId: number;
  restaurantName?: string;
  orderCode?: string | number;
  returnUrl?: string;
}): Promise<PaymentView | { redirected: true } | null> {
  const open = await getOrderPayment(opts.orderId, opts.restroId);
  if (!open || !isOpenPaymentStatus(open.status) || !open.checkout?.type) {
    return null;
  }
  return launchGuestCheckout(open, {
    name: opts.restaurantName,
    description: `Order ${opts.orderCode || opts.orderId}`,
  });
}
